import React, { useEffect } from 'react';
import '@trp-beacon-ui-kit/foundation/style-tokens/css/variables.css';
import '@trp-beacon-ui-kit/foundation/fonts/fonts.css';
import { Box, Typography } from '@trp-individual-investor-adv-svc/ii-prosp-components';
import { PsBeaconButton, PsBeaconIcon, PsBeaconIconButton, PsBeaconTooltip } from '../../shared/BeaconRegister';
import GenericTile from './GenericTile';
import documentIcon from '../../../assets/images/DocumentIcon.svg';
import { Content } from '../../shared/Content';
import { axiosDocumentInstance } from '../../../actions/AxiosInstance';
import api from '../../../actions/EndPoints';
import { dateFormatter, log, setLastRequestTime } from '../../../Utils';
import { APPLICATION_PDF } from '../../../Constants';

const PastPlanTile = ({ state, setServiceError, setPopupError, fullHeight }) => {
    const { apiKey } = state;
    const smallScreenSize = window.matchMedia('(max-width: 960px)');
    const [smallScreen, setSmallScreen] = React.useState(window.innerWidth <= 960 ? true : false);
    const DATE_FORMAT = 'MM-DD-YYYY';
    const hasRiStrategyCompPlan = state?.planningGroup?.latestReleasedStrategyComparison;
    const hasRiSelectedStrategyPlan = state?.planningGroup?.latestReleasedSelectedStrategy;
    const externalGroupId = state?.planningGroup?.externalId;

    useEffect(() => {
        const handleResize = () => {
            setSmallScreen(smallScreenSize);
        };
        smallScreenSize.addEventListener('change', handleResize);
        return () => smallScreenSize.removeEventListener('change', handleResize);
    }, []);

    async function retrievePlan(e) {
        log(console.log('Calling retrieve plan'));

        if (e) {
            try {
                const getPlan = await axiosDocumentInstance.get(
                    `${api.getLatestPlanAPI(externalGroupId)}?docType=FINANCIAL_PLAN&allEngagements=true`,
                    {
                        responseType: 'arraybuffer',
                        headers: {
                            apikey: `${apiKey}`,
                            Accept: APPLICATION_PDF,
                        },
                    }
                );

                if (getPlan) {
                    const { data, headers } = getPlan;
                    setLastRequestTime();
                    const lastModifiedTimeStamp = headers['last-modified'];
                    if (data !== undefined) {
                        const planBlob = new Blob([data], { type: APPLICATION_PDF });
                        if (window?.navigator?.msSaveOrOpenBlob) {
                            // IE and Edge browser use msSaveOrOpenBlob
                            window.navigator.msSaveOrOpenBlob(
                                planBlob,
                                `Personalized_Plan_${dateFormatter(
                                    lastModifiedTimeStamp,
                                    DATE_FORMAT
                                )}.pdf`
                            );
                        } else {
                            try {
                                const link = document.createElement('a');
                                link.href = window.URL.createObjectURL(planBlob);
                                link.download = `Personalized_Plan_${dateFormatter(
                                    lastModifiedTimeStamp,
                                    DATE_FORMAT
                                )}.pdf`;
                                link.click();
                                setTimeout(() => {
                                    window.URL.revokeObjectURL(data);
                                }, 100);
                            } catch (error) {
                                log(console.log(error));
                                setPopupError(true);
                            }
                        }
                    }
                }
            } catch (err) {
                setServiceError(true);
                console.log(err);
            }
        }
    }

    async function retrieveRICMPlan(docType) {
        log(console.log('Calling retrieve RICM plan: ' + docType));
        setServiceError(false);
        try {
            const getPlan = await axiosDocumentInstance.get(
                `${api.getLatestPlanAPI(externalGroupId)}?docType=${docType}&allEngagements=true`,
                {
                    responseType: 'arraybuffer',
                    headers: {
                        apikey: `${apiKey}`,
                        Accept: APPLICATION_PDF,
                    },
                }
            );

            if (getPlan) {
                const { data, headers } = getPlan;
                const lastModifiedTimeStamp = headers['last-modified'];
                if (data !== undefined) {
                    const planBlob = new Blob([data], { type: APPLICATION_PDF });
                    if (window?.navigator?.msSaveOrOpenBlob) {
                        // IE and Edge browser use msSaveOrOpenBlob
                        window.navigator.msSaveOrOpenBlob(
                            planBlob,
                            `Personalized_Plan__${docType}__${dateFormatter(
                                lastModifiedTimeStamp,
                                DATE_FORMAT
                            )}.pdf`
                        );
                    } else {
                        try {
                            const link = document.createElement('a');
                            link.href = window.URL.createObjectURL(planBlob);
                            link.download = `Personalized_Plan_RICM__${docType}__${dateFormatter(
                                lastModifiedTimeStamp,
                                DATE_FORMAT
                            )}.pdf`;
                            link.click();
                            setTimeout(() => {
                                window.URL.revokeObjectURL(data);
                            }, 100);
                        } catch (e) {
                            log(console.log(e));
                            setPopupError(true);
                        }
                    }
                }
            }
        } catch (err) {
            setServiceError(true);
            console.log(err);
        }
    }

    function handleDownloadRicmPlans() {
        if (hasRiStrategyCompPlan) {
            retrieveRICMPlan('STRATEGY_COMPARISON');
        }
        if (hasRiSelectedStrategyPlan) {
            retrieveRICMPlan('SELECTED_STRATEGY');
        }
    }

    return (
        <GenericTile
            buttonVariant="secondary"
            title={
                hasRiStrategyCompPlan || hasRiSelectedStrategyPlan
                    ? 'View latest plan documents'
                    : 'View your plan'
            }
            description="We’ve designed this plan to help you achieve your financial goals."
            id="view-expired-plan"
            smallScreenSize={smallScreen}
            icon={documentIcon}
            fullHeight={fullHeight}>
            <Box id='button-container' sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <Box id="view-plan-button-container" sx={{ display: 'flex', paddingTop: '32px', width: '100%' }}>
                    <Box>
                        <PsBeaconButton variant="secondary" onClick={(e) => retrievePlan(e)}>
                            {Content.FpsCardAction.viewPlan.mainButtonText}
                        </PsBeaconButton>
                    </Box>
                    <Box
                        sx={{ alignContent: 'center', paddingLeft: '12px' }}
                        id="view-plan-tooltip-button-container">
                        <PsBeaconIconButton variant='tertiary' id="view-plan-tooltip-button" aria-label="help tooltip">
                            Help Tooltip Icon for Financial Plan
                            <PsBeaconIcon name='help' slot='icon' aria-label='help icon' />
                        </PsBeaconIconButton>
                        <PsBeaconTooltip
                            id="financial-plan-tooltip"
                            placement="right"
                            opens-by="#view-plan-tooltip-button"
                            className="beacon-on-dark"
                            style={{ minWidth: '250px', padding: '16px', textAlign: 'left' }}>
                            <Typography
                                id="view-plan-tooltip-title"
                                sx={{ paddingBottom: '16px' }}
                                type="body" scale={100} bold>
                                {Content.FpsCardAction.viewPlan.tooltipTitle}
                            </Typography>
                            <Typography
                                id="view-plan-tooltip-text"
                                type="body" scale={100} light>
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: Content.FpsCardAction.viewPlan.tooltipText,
                                    }}
                                />
                            </Typography>
                        </PsBeaconTooltip>
                    </Box>
                </Box>
                {(hasRiStrategyCompPlan || hasRiSelectedStrategyPlan) && (
                    <Box
                        id="view-ricm-strat-comp-button-container"
                        sx={{ display: "flex", paddingTop: '12px' }}>
                        <PsBeaconButton variant="secondary" onClick={handleDownloadRicmPlans}>
                            {Content.FpsCardAction.viewPlan.secondaryMainButtonText}
                        </PsBeaconButton>
                        <Box
                            sx={{ alignContent: 'center', paddingLeft: '12px' }}
                            id="view-ricm-tooltip-button-container">
                            <PsBeaconIconButton variant='tertiary' id="view-ricm-tooltip-button">
                                Help Tooltip Icon for Retirement Income Plan
                                <PsBeaconIcon name='help' slot='icon' />
                            </PsBeaconIconButton>
                            <PsBeaconTooltip
                                id="ricm-tooltip"
                                placement="right"
                                opens-by="#view-ricm-tooltip-button"
                                className="beacon-on-dark"
                                style={{ minWidth: '250px', padding: '16px', textAlign: 'left' }}>
                                <Typography
                                    id="ricm-plan-tooltip-title"
                                    sx={{ paddingBottom: '16px' }}
                                    type="body" scale={100} bold>
                                    {Content.FpsCardAction.viewPlan.secondaryTooltipTitle}
                                </Typography>
                                <Typography
                                    id="ricm-plan-tooltip-text"
                                    type="body" scale={100} light>
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: Content.FpsCardAction.viewPlan.secondaryTooltipText,
                                        }}
                                    />
                                </Typography>
                            </PsBeaconTooltip>
                        </Box>
                    </Box>
                )}
            </Box>
        </GenericTile>
    );
};

export default PastPlanTile;
